import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { http } from "@/lib/api";

// Lote 150 — "Calendários protegidos" (Administração). O calendário assinado (Google/Outlook/Apple) é um espelho:
// se ele encolher de uma vez, o servidor congela na última versão boa e espera a confirmação do administrador aqui.
const fmt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
};

export default function CalendariosProtegidosCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => http.get("/admin/ics-freios").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const confirmar = async (uid) => {
    setBusy(uid);
    try {
      await http.post(`/admin/ics-freios/${uid}/confirmar`);
      toast.success("Redução confirmada", { description: "O calendário dessa conta voltou a refletir a agenda atual." });
      await load();
    } catch {
      toast.error("Não consegui confirmar agora");
    } finally { setBusy(""); }
  };

  if (!st) return null;
  const cong = st.congelados || [];
  const foto = st.ultima_foto;
  return (
    <section data-testid="admin-calendarios-protegidos" className="bg-white border border-stone-200 rounded-md p-5 mb-6">
      <h2 className="font-display text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
        <CalendarCheck className="w-5 h-5 text-stone-500" strokeWidth={1.75} /> Calendários protegidos
      </h2>
      <p className="text-sm text-stone-500 mb-3 max-w-2xl">
        Se o calendário assinado de uma conta encolher de uma vez, o IApauta continua entregando a última versão boa até você confirmar.
        {" "}{st.total_protegidos} {st.total_protegidos === 1 ? "calendário vigiado" : "calendários vigiados"}.
      </p>

      {cong.length === 0 ? (
        <p data-testid="calendarios-ok" className="text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} /> Nenhum calendário congelado.
        </p>
      ) : (
        <ul className="space-y-2 mb-2" data-testid="calendarios-congelados">
          {cong.map((c) => (
            <li key={c.user_id} data-testid={`calendario-congelado-${c.user_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="flex items-start gap-2 min-w-0">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                <span>
                  <b>{c.nome || c.email || c.user_id}</b> — de {c.n_bom} para {c.n_novo} eventos
                  {c.desde ? <span className="text-amber-800/80"> (desde {fmt(c.desde)})</span> : null}
                  <span className="block text-xs text-amber-800/80">Quem assina continua vendo a versão anterior. Confirme só se a redução foi de propósito.</span>
                </span>
              </span>
              <Button type="button" size="sm" data-testid={`confirmar-reducao-${c.user_id}`} disabled={busy === c.user_id}
                onClick={() => confirmar(c.user_id)} className="bg-stone-900 hover:bg-stone-800 text-white">
                {busy === c.user_id ? "Confirmando…" : "Confirmar redução"}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p data-testid="calendarios-foto" className="text-xs text-stone-500 mt-3">
        {!st.fotos_configuradas
          ? "Foto diária na nuvem: não configurada neste servidor."
          : foto
            ? `Foto diária cifrada na nuvem (40 dias): última em ${fmt(foto.quando)} — ${foto.ok} ${foto.ok === 1 ? "calendário" : "calendários"}${foto.falhas ? `, ${foto.falhas} com falha` : ""}.`
            : "Foto diária cifrada na nuvem: a primeira sai hoje às 7h40."}
      </p>
    </section>
  );
}
